import type { Options, ResponsePromise } from 'ky'
import type { IHttpClient } from '../Contracts/Services/IHttpClient'
import type { IResourceConfig } from '../Contracts/UI/Resource'
import { GetResourceTask } from '../Tasks/Resources/GetResourceTask'
import { GetRestUrlTask } from '../Tasks/Resources/GetRestUrlTask'
import type { CollectionTransformer } from './ResourceClientService/CollectionTransformers'
import { CollectionTransformers } from './ResourceClientService/CollectionTransformers'
import type {
    IResourceClientService,
    IResourceRequestSettings,
} from './ResourceClientService/ResourceClientService.types'
import { restClient } from './RestClientService'
import type { HTTPResponseStore } from './RestClientService/HTTPResponseStore'

/**
 * Сервіс для роботи з API Platform ресурсами
 * Обгортає IHttpClient та автоматично обробляє hydra:member формат
 *
 * @description
 * ResourceClientService - високорівнева обгортка для роботи з ресурсами:
 * - Працює з конфігураціями ресурсів (IResourceConfig)
 * - Автоматично будує URL через GetRestUrlTask
 * - Спеціалізована обробка колекцій (hydra:member, data тощо)
 * - Не залежить від конкретної реалізації HTTP клієнта (використовує IHttpClient)
 *
 * @example
 * ```typescript
 * // Отримати колекцію з автоматичним hydra:member розпакуванням
 * const response = await resourceClient.getCollection('products')
 *
 * // Отримати конкретний елемент
 * const item = await resourceClient.get('products', {
 *   searchParams: { id: 123 }
 * }, {
 *   restUrlReplaceParams: { id: 123 }
 * })
 *
 * // Створити новий елемент
 * const newItem = await resourceClient.post('products', {
 *   json: { name: 'Product', price: 100 }
 * })
 *
 * // Оновити елемент (PATCH)
 * const updated = await resourceClient.patch('products', {
 *   json: { price: 150 }
 * }, {
 *   restUrlReplaceParams: { id: 123 }
 * })
 * ```
 */
export class ResourceClientService implements IResourceClientService {
    /**
     * HTTP клієнт для виконання запитів
     * Використовуємо інтерфейс для незалежності від конкретної реалізації
     */
    private httpClient: IHttpClient

    /**
     * Конструктор ResourceClientService
     *
     * @param httpClient - HTTP клієнт (за замовчуванням restClient)
     *
     * @example
     * ```typescript
     * // З дефолтним клієнтом
     * const resourceClient = new ResourceClientService()
     *
     * // З кастомним клієнтом
     * const customClient = new CustomHttpClient()
     * const resourceClient = new ResourceClientService(customClient)
     * ```
     */
    constructor(httpClient?: IHttpClient) {
        this.httpClient = httpClient || restClient
    }
    /**
     * Отримує колекцію ресурсів з автоматичним розпакуванням hydra:member
     * Повертає Promise з масивом об'єктів
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з колекцією ресурсів
     */
    getCollection<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const opts = this.prepareOptionsForCollection(options, settings)
        const url = this.getUrlByResource(resource, 'getCollection', settings)

        if (!url) {
            return null
        }

        return this.httpClient.get<T>(url, opts, settings)
    }

    /**
     * Отримує колекцію ресурсів з MobX store та автоматичним розпакуванням hydra:member
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    getCollectionStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const opts = this.prepareOptionsForCollection(options, settings)
        const url = this.getUrlByResource(resource, 'getCollection', settings)

        if (!url) {
            return null
        }

        return this.httpClient.getStore(url, opts, settings)
    }

    /**
     * Отримує конкретний ресурс за його ID
     * Повертає Promise з об'єктом
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns Promise з ресурсом
     */
    get<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, 'get', settings)

        if (!url) {
            return null
        }

        return this.httpClient.get<T>(url, options, settings)
    }

    /**
     * Отримує конкретний ресурс з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns HTTPResponseStore з MobX реактивністю
     */
    getStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, 'get', settings)

        if (!url) {
            return null
        }

        return this.httpClient.getStore(url, options, settings)
    }

    /**
     * Виконує кастомний GET запит за альтернативним URI endpoint
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю
     */
    getBy<T = unknown>(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.get<T>(url, options, settings)
    }

    /**
     * Виконує кастомний GET запит з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    getByStore(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.getStore(url, options, settings)
    }

    /**
     * Створює новий ресурс (POST)
     * Повертає Promise з створеним об'єктом
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта
     * @returns Promise з створеним ресурсом
     */
    post<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, 'post', settings)

        if (!url) {
            return null
        }

        return this.httpClient.post<T>(url, options, settings)
    }

    /**
     * Створює новий ресурс з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    postStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, 'post', settings)

        if (!url) {
            return null
        }

        return this.httpClient.postStore(url, options, settings)
    }

    /**
     * Виконує кастомний POST запит за альтернативним URI endpoint
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю
     */
    postBy<T = unknown>(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.post<T>(url, options, settings)
    }

    /**
     * Виконує кастомний POST запит з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    postByStore(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.postStore(url, options, settings)
    }

    /**
     * Повністю оновлює ресурс (PUT)
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns Promise з оновленим ресурсом
     */
    put<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, 'put', settings)

        if (!url) {
            return null
        }

        return this.httpClient.put<T>(url, options, settings)
    }

    /**
     * Повністю оновлює ресурс з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns HTTPResponseStore з MobX реактивністю
     */
    putStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, 'put', settings)

        if (!url) {
            return null
        }

        return this.httpClient.putStore(url, options, settings)
    }

    /**
     * Частково оновлює ресурс (PATCH) з автоматичним Content-Type
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns Promise з оновленим ресурсом
     */
    patch<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, 'patch', settings)

        if (!url) {
            return null
        }

        return this.httpClient.patch<T>(url, options, settings)
    }

    /**
     * Частково оновлює ресурс з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky (включає json/body)
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns HTTPResponseStore з MobX реактивністю
     */
    patchStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, 'patch', settings)

        if (!url) {
            return null
        }

        return this.httpClient.patchStore(url, options, settings)
    }

    /**
     * Виконує кастомний PATCH запит за альтернативним URI endpoint
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю
     */
    patchBy<T = unknown>(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.patch<T>(url, options, settings)
    }

    /**
     * Виконує кастомний PATCH запит з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    patchByStore(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.patchStore(url, options, settings)
    }

    /**
     * Видаляє ресурс (DELETE)
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns Promise з відповіддю
     */
    delete<T = unknown>(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, 'delete', settings)

        if (!url) {
            return null
        }

        return this.httpClient.delete<T>(url, options, settings)
    }

    /**
     * Видаляє ресурс з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта (обов'язково restUrlReplaceParams.id)
     * @returns HTTPResponseStore з MobX реактивністю
     */
    deleteStore(
        resource: string | IResourceConfig,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, 'delete', settings)

        if (!url) {
            return null
        }

        return this.httpClient.deleteStore(url, options, settings)
    }

    /**
     * Виконує кастомний DELETE запит за альтернативним URI endpoint
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns Promise з відповіддю
     */
    deleteBy<T = unknown>(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): ResponsePromise<T> | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.delete<T>(url, options, settings)
    }

    /**
     * Виконує кастомний DELETE запит з MobX store
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param uriCode - код альтернативного endpoint в restUrl конфігурації
     * @param options - опції запиту з Ky
     * @param settings - налаштування клієнта
     * @returns HTTPResponseStore з MobX реактивністю
     */
    deleteByStore(
        resource: string | IResourceConfig,
        uriCode: string,
        options?: Options,
        settings?: IResourceRequestSettings
    ): HTTPResponseStore | null {
        const url = this.getUrlByResource(resource, uriCode, settings)

        if (!url) {
            return null
        }

        return this.httpClient.deleteStore(url, options, settings)
    }

    /**
     * Отримує URL для ресурсу за його конфігурацією та режимом
     *
     * @param resource - ключ ресурсу або об'єкт конфігурації
     * @param mode - режим операції (getCollection, get, post, patch, delete або кастомний)
     * @param settings - налаштування з параметрами для заміни в URI
     * @returns готовий URL або null якщо ресурс не знайдено
     */
    private getUrlByResource(
        resource: string | IResourceConfig,
        mode: string,
        settings?: IResourceRequestSettings
    ): string | null {
        const resourceConfig = GetResourceTask(resource)

        if (!resourceConfig) {
            console.warn('Resource not found:', resource)
            return null
        }

        const restUrlReplaceParams = settings?.restUrlReplaceParams ?? {}
        return GetRestUrlTask(resourceConfig.restUrl, mode, restUrlReplaceParams)
    }

    /**
     * Підготовлює опції для запиту колекції з кастомізованою обробкою відповіді
     *
     * @param options - базові опції запиту
     * @param settings - налаштування з можливістю вказати трансформер
     * @returns опції з доданим afterResponse хуком для трансформації відповіді
     */
    private prepareOptionsForCollection(options?: Options, settings?: IResourceRequestSettings): Options {
        const opts = options ?? {}

        // Якщо трансформація вимкнена - повертаємо опції без змін
        if (settings?.disableCollectionTransform) {
            return opts
        }

        // Ініціалізуємо hooks якщо їх немає
        if (!opts.hooks) {
            opts.hooks = {}
        }

        // Ініціалізуємо afterResponse як масив якщо його немає
        if (!Array.isArray(opts.hooks.afterResponse)) {
            opts.hooks.afterResponse = []
        }

        // Визначаємо який трансформер використовувати
        let transformer: CollectionTransformer

        if (settings?.collectionTransformer) {
            // Якщо передана функція - використовуємо її
            if (typeof settings.collectionTransformer === 'function') {
                transformer = settings.collectionTransformer
            } else {
                // Якщо передана назва - беремо з вбудованих
                transformer = CollectionTransformers[settings.collectionTransformer] || CollectionTransformers.none
            }
        } else {
            // За замовчуванням використовуємо apiPlatform трансформер
            transformer = CollectionTransformers.none
        }

        // Додаємо хук для трансформації відповіді
        opts.hooks.afterResponse.push(async (request: Request, options: any, response: Response) => {
            if (response.ok) {
                return response.json().then((data) => {
                    // Застосовуємо трансформер
                    const transformed = transformer(data)

                    // Визначаємо фінальні дані для відповіді
                    let finalData: any

                    if (transformed && typeof transformed === 'object' && 'items' in transformed) {
                        // Якщо трансформер повернув об'єкт з items та meta
                        // Повертаємо тільки items для зворотної сумісності
                        // Але зберігаємо meta в headers для можливого використання
                        finalData = transformed.items

                        // Можна додати meta до response headers якщо потрібно
                        // (опціонально, для доступу до метаданих без парсингу body)
                    } else {
                        // Інакше повертаємо як є (масив або оригінальні дані)
                        finalData = transformed
                    }

                    // Повертаємо модифіковану відповідь
                    return new Response(JSON.stringify(finalData), {
                        status: response.status,
                        statusText: response.statusText,
                        headers: response.headers,
                    })
                })
            }

            return response
        })

        return opts
    }
}

/**
 * Глобальний екземпляр ResourceClientService
 * Використовується для роботи з API Platform ресурсами
 *
 * @example
 * ```typescript
 * import { resourceClient } from '@/Core/Services/ResourceClientService'
 *
 * // Отримати колекцію
 * const products = await resourceClient.getCollection('products')
 *
 * // Створити ресурс
 * const newProduct = await resourceClient.post('products', {
 *   json: { name: 'New Product', price: 100 }
 * })
 * ```
 */
export const resourceClient = new ResourceClientService()
